import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, ArrowLeft, Download, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { companiesImportApi } from '../lib/api';

/**
 * Import massivo aziende da Excel (foglio "Worksheet" + "ateco").
 * Flow: upload → preview → conferma → report.
 */
const ImportAziendePage = () => {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [showTab, setShowTab] = useState('overview');

  const onFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Serve un file .xlsx');
      return;
    }
    setFile(f);
    setSession(null);
    setConfirmed(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await companiesImportApi.preview(fd);
      setSession(res.data);
      toast.success(`Anteprima pronta: ${res.data.stats.letto} righe lette`);
      setShowTab('overview');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore lettura file');
    } finally {
      setLoading(false);
    }
  };

  const runConfirm = async () => {
    if (!session?.sessionId) return;
    if (!window.confirm(`Confermi l'importazione di ${session.stats.letto} righe? Verranno create ${session.stats.nuove} e aggiornate ${session.stats.aggiornate}.`)) return;
    setLoading(true);
    try {
      const res = await companiesImportApi.confirm(session.sessionId);
      setConfirmed(res.data);
      toast.success(`Import completato: ${res.data.created} create, ${res.data.updated} aggiornate`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore importazione');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!session?.sessionId) return;
    try {
      await companiesImportApi.downloadReport(session.sessionId);
    } catch (e) {
      toast.error('Report non disponibile');
    }
  };

  const stats = session?.stats;

  return (
    <div className="space-y-5" data-testid="import-aziende-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-700">
          <ArrowLeft size={15} /> Indietro
        </button>
        <Link to="/aziende" className="text-sm text-slate-500 hover:text-[#023F80]">Aziende</Link>
        <span className="text-slate-300">/</span>
        <span className="text-sm font-semibold text-slate-800">Import massivo</span>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#023F80] to-[#022F60] text-white p-6 sm:p-8">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#FFCB05]/15" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#FFCB05] text-[#023F80] flex items-center justify-center"><FileSpreadsheet size={22} /></div>
            <div>
              <h1 className="text-2xl font-extrabold">Import massivo aziende</h1>
              <p className="text-white/70 text-sm">Carica il file Excel per aggiornare anagrafica e profilazione. Match per Partita IVA → Codice Fiscale → Denominazione normalizzata.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload step */}
      {!session && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2"><Upload size={16} className="text-[#023F80]" /> 1. Carica il file</h2>
          <div
            className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-[#023F80] transition-colors cursor-pointer bg-slate-50/40"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          >
            <FileSpreadsheet size={36} className="mx-auto text-[#023F80] mb-2" />
            <div className="text-sm font-semibold text-slate-700">{file ? file.name : 'Clicca o trascina qui il file .xlsx'}</div>
            <div className="text-xs text-slate-500 mt-1">Deve contenere i fogli <b>Worksheet</b> e <b>ateco</b></div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="import-file-input"
            />
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={runPreview}
              disabled={!file || loading}
              data-testid="import-preview-btn"
              className="px-5 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} className="text-[#FFCB05]" />}
              Analizza file
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {session && !confirmed && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard label="Righe lette" value={stats.letto} color="#023F80" bg="#e0e7ff" />
            <StatCard label="Nuove" value={stats.nuove} color="#0e7c3a" bg="#dcfce7" />
            <StatCard label="Aggiornate" value={stats.aggiornate} color="#8a6608" bg="#fef7d4" />
            <StatCard label="Invariate" value={stats.invariate} color="#525252" bg="#f4f4f5" />
            <StatCard label="Duplicati" value={stats.duplicati_nell_excel} color="#b45309" bg="#fef3c7" />
            <StatCard label="Anomalie" value={stats.anomalie} color="#b91c1c" bg="#fee2e2" />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="border-b border-slate-100 px-4 py-2 flex flex-wrap gap-1">
              {[
                { k: 'overview', label: `Anteprima (${session.sample?.length || 0})` },
                { k: 'anomalies', label: `Anomalie (${stats.anomalie})` },
                { k: 'onlyapp', label: `Solo nell'app (${session.onlyInAppCount})` },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setShowTab(t.k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${showTab === t.k ? 'bg-[#023F80] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`import-tab-${t.k}`}
                >
                  {t.label}
                </button>
              ))}
              <div className="ml-auto flex gap-2">
                <button
                  onClick={downloadReport}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center gap-1"
                >
                  <Download size={13} /> Report XLSX
                </button>
                <button
                  onClick={runConfirm}
                  disabled={loading}
                  data-testid="import-confirm-btn"
                  className="px-4 py-1.5 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="text-[#FFCB05]" />}
                  Conferma importazione
                </button>
              </div>
            </div>

            <div className="p-4 overflow-x-auto">
              {showTab === 'overview' && <SampleTable items={session.sample || []} />}
              {showTab === 'anomalies' && <AnomaliesTable items={(session.sample || []).filter((i) => i.anomalies?.length)} />}
              {showTab === 'onlyapp' && <OnlyInAppTable items={session.onlyInApp || []} />}
            </div>
          </div>
        </>
      )}

      {/* Confirmation done */}
      {confirmed && (
        <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 size={22} /></div>
            <div>
              <h2 className="text-base font-bold text-emerald-800">Import completato</h2>
              <p className="text-xs text-slate-600">Le modifiche sono state salvate nel database.</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatCard label="Create" value={confirmed.created} color="#0e7c3a" bg="#dcfce7" />
            <StatCard label="Aggiornate" value={confirmed.updated} color="#8a6608" bg="#fef7d4" />
            <StatCard label="Saltate" value={confirmed.skipped} color="#525252" bg="#f4f4f5" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadReport} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700 flex items-center gap-2">
              <Download size={14} /> Scarica report XLSX
            </button>
            <button onClick={() => navigate('/aziende')} className="px-4 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-xs font-bold flex items-center gap-2">
              Vai a Elenco aziende
            </button>
            <button
              onClick={() => { setFile(null); setSession(null); setConfirmed(null); }}
              className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700"
            >
              Nuovo import
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, bg }) => (
  <div className="rounded-xl p-3" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</div>
    <div className="text-2xl font-extrabold mt-1" style={{ color }}>{value ?? 0}</div>
  </div>
);

const actionLabel = {
  create: { label: 'Nuova', bg: 'bg-emerald-100', color: 'text-emerald-800' },
  update: { label: 'Aggiorna', bg: 'bg-amber-100', color: 'text-amber-800' },
  unchanged: { label: 'Invariata', bg: 'bg-slate-100', color: 'text-slate-600' },
};

const SampleTable = ({ items }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-[11px] uppercase tracking-wider text-gray-500">
        <th className="text-left bg-gray-100 rounded-l-xl px-3 py-2.5">Riga</th>
        <th className="text-left bg-gray-100 px-3 py-2.5">Azione</th>
        <th className="text-left bg-gray-100 px-3 py-2.5">Denominazione</th>
        <th className="text-left bg-gray-100 px-3 py-2.5">P.IVA</th>
        <th className="text-left bg-gray-100 px-3 py-2.5">CF</th>
        <th className="text-left bg-gray-100 px-3 py-2.5">Match</th>
        <th className="text-left bg-gray-100 rounded-r-xl px-3 py-2.5">Campi aggiornati</th>
      </tr>
    </thead>
    <tbody>
      {items.map((it) => {
        const meta = actionLabel[it.action] || actionLabel.unchanged;
        return (
          <tr key={it.row} className="border-b border-gray-50">
            <td className="px-3 py-2 text-slate-600">{it.row}</td>
            <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.bg} ${meta.color}`}>{meta.label}</span></td>
            <td className="px-3 py-2 font-semibold text-slate-800">{it.name}</td>
            <td className="px-3 py-2 font-mono text-xs">{it.piva || '—'}</td>
            <td className="px-3 py-2 font-mono text-xs">{it.codiceFiscale || '—'}</td>
            <td className="px-3 py-2 text-xs text-slate-500">{it.matchedBy || '—'}</td>
            <td className="px-3 py-2 text-xs text-slate-600 max-w-md">
              {it.anomalies?.length > 0 && (
                <span className="inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-bold">
                  <AlertTriangle size={11} /> {it.anomalies.length}
                </span>
              )}
              {(it.changed || []).slice(0, 6).map((c) => (
                <span key={c} className="inline-block bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 text-[10px] mr-1 mb-0.5">{c}</span>
              ))}
              {(it.changed || []).length > 6 && <span className="text-[10px] text-slate-500">+{it.changed.length - 6}</span>}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const AnomaliesTable = ({ items }) => {
  if (items.length === 0) return <div className="py-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2"><CheckCircle2 size={16} className="text-emerald-600" /> Nessuna anomalia rilevata</div>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[11px] uppercase tracking-wider text-gray-500">
          <th className="text-left bg-gray-100 rounded-l-xl px-3 py-2.5">Riga</th>
          <th className="text-left bg-gray-100 px-3 py-2.5">Denominazione</th>
          <th className="text-left bg-gray-100 rounded-r-xl px-3 py-2.5">Anomalie</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.row} className="border-b border-gray-50">
            <td className="px-3 py-2 text-slate-600">{it.row}</td>
            <td className="px-3 py-2 font-semibold text-slate-800">{it.name}</td>
            <td className="px-3 py-2 text-xs">
              {it.anomalies.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 mr-1 mb-0.5 px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-semibold">
                  <XCircle size={11} /> {a.type}{a.value ? `: ${a.value}` : ''}
                </span>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const OnlyInAppTable = ({ items }) => {
  if (!items.length) return <div className="py-8 text-center text-sm text-slate-500">Nessuna azienda esclusiva dell&apos;app</div>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[11px] uppercase tracking-wider text-gray-500">
          <th className="text-left bg-gray-100 rounded-l-xl px-3 py-2.5">Denominazione</th>
          <th className="text-left bg-gray-100 px-3 py-2.5">P.IVA</th>
          <th className="text-left bg-gray-100 rounded-r-xl px-3 py-2.5">CF</th>
        </tr>
      </thead>
      <tbody>
        {items.map((c) => (
          <tr key={c.id} className="border-b border-gray-50">
            <td className="px-3 py-2 font-semibold text-slate-800">{c.name}</td>
            <td className="px-3 py-2 font-mono text-xs">{c.piva || '—'}</td>
            <td className="px-3 py-2 font-mono text-xs">{c.codiceFiscale || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ImportAziendePage;
